import React, { useState } from 'react';
import {
    List, Datagrid, TextField, EditButton, DeleteButton,
    Edit, Create, SimpleForm, TextInput, NumberInput,
    useNotify, usePermissions, useRecordContext,
    SaveButton, Toolbar, required,
} from 'react-admin';
import { useController } from 'react-hook-form';
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useGithubConfig, GitHubImage } from './ra_contentEditor';
import { deleteFile, getFile, putTextFile, uploadBinary } from './githubApi';
import { getDocs, collection } from 'firebase/firestore';
import { db } from './firebase';

// ---------------------------------------------------------------------------
// Sync: regenerate services/services.json from Firestore
// ---------------------------------------------------------------------------

export async function syncServicesJson(ghConfig) {
    const { pat, owner, repo, branch } = ghConfig;
    const snap = await getDocs(collection(db, 'services'));
    const services = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    let sha = null;
    try { ({ sha } = await getFile(pat, owner, repo, 'services/services.json', branch)); } catch {}
    await putTextFile(pat, owner, repo, 'services/services.json', branch,
        JSON.stringify(services, null, 2), sha, '[admin] sync services.json from Firestore');
    return services;
}

// Strip undefined so Firestore never rejects the payload
const sanitizeService = (values) => ({
    name: values.name ?? '',
    description: values.description ?? '',
    image: values.image ?? '',
    order: values.order ?? 0,
});

// ---------------------------------------------------------------------------
// Image widget: preview + upload + delete file from GitHub
// ---------------------------------------------------------------------------

function ServiceImageField() {
    const record = useRecordContext();
    const notify = useNotify();
    const { ghConfig } = useGithubConfig();
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [buster, setBuster] = useState(0);
    const ref = React.useRef();

    // Access the form's image field so we can clear it on delete
    const { field: imageField } = useController({ name: 'image', defaultValue: record?.image ?? '' });
    const currentImage = imageField.value;

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !ghConfig) return;
        setUploading(true);
        try {
            const buf = await file.arrayBuffer();
            let sha = null;
            try { ({ sha } = await getFile(ghConfig.pat, ghConfig.owner, ghConfig.repo, `services/${file.name}`, ghConfig.branch)); } catch {}
            await uploadBinary(ghConfig.pat, ghConfig.owner, ghConfig.repo, `services/${file.name}`, ghConfig.branch, buf, sha, `[admin] upload service image ${file.name}`);
            imageField.onChange(file.name);
            setBuster(b => b + 1);
            notify(`Uploaded ${file.name} and set as icon.`, { type: 'success' });
        } catch (err) {
            notify(`Upload failed: ${err.message}`, { type: 'error' });
        }
        setUploading(false);
        e.target.value = '';
    };

    const handleDelete = async () => {
        if (!currentImage || !ghConfig) return;
        setDeleting(true);
        try {
            const { sha } = await getFile(ghConfig.pat, ghConfig.owner, ghConfig.repo, `services/${currentImage}`, ghConfig.branch);
            await deleteFile(ghConfig.pat, ghConfig.owner, ghConfig.repo, `services/${currentImage}`, ghConfig.branch, sha, `[admin] delete service image ${currentImage}`);
            imageField.onChange('');
            setBuster(b => b + 1);
            notify(`Deleted ${currentImage}.`, { type: 'success' });
        } catch (err) {
            notify(`Delete failed: ${err.message}`, { type: 'error' });
        }
        setDeleting(false);
    };

    return (
        <Box mb={1}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Icon image</Typography>
            {currentImage && ghConfig && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Box sx={{ width: 100, height: 75, border: '1px solid #ccc', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
                        <GitHubImage key={buster} pat={ghConfig.pat} owner={ghConfig.owner} repo={ghConfig.repo}
                            branch={ghConfig.branch} filePath={`services/${currentImage}`}
                            imgStyle={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </Box>
                    <Box>
                        <Typography variant="body2">{currentImage}</Typography>
                        <Tooltip title="Delete file from GitHub and clear field">
                            <span>
                                <IconButton size="small" color="error" onClick={handleDelete} disabled={deleting}>
                                    {deleting ? <CircularProgress size={16} /> : <DeleteForeverIcon fontSize="small" />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            )}
            <Box display="flex" gap={1} alignItems="center">
                <input type="file" accept="image/*" ref={ref} style={{ display: 'none' }} onChange={handleUpload} />
                <Button variant="outlined" size="small"
                    startIcon={uploading ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    onClick={() => ref.current.click()} disabled={uploading}>
                    {uploading ? 'Uploading…' : currentImage ? 'Replace image' : 'Upload image'}
                </Button>
            </Box>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// Auto-sync toolbar
// ---------------------------------------------------------------------------

const SyncSaveToolbar = () => {
    const { ghConfig } = useGithubConfig();
    const notify = useNotify();
    return (
        <Toolbar>
            <SaveButton
                label="Save & sync"
                mutationOptions={{
                    onSuccess: async () => {
                        if (!ghConfig) {
                            notify('Saved, but GitHub isn\'t configured — site content was not synced.', { type: 'warning' });
                            return;
                        }
                        try {
                            await syncServicesJson(ghConfig);
                            notify('Saved and synced — site content updated.', { type: 'success' });
                        } catch (e) {
                            notify(`Saved but sync failed: ${e.message}`, { type: 'warning' });
                        }
                    },
                }}
            />
        </Toolbar>
    );
};

// ---------------------------------------------------------------------------
// CRUD components
// ---------------------------------------------------------------------------

const SyncDeleteButton = () => {
    const { ghConfig } = useGithubConfig();
    return (
        <DeleteButton mutationOptions={{
            onSuccess: async () => {
                if (ghConfig) await syncServicesJson(ghConfig).catch(() => {});
            }
        }} />
    );
};

export const ServicesList = (props) => {
    const { permissions } = usePermissions();
    return (
        <List {...props} title="Services" sort={{ field: 'order', order: 'ASC' }}>
            <Datagrid rowClick="edit">
                <TextField source="name" label="Service" />
                <TextField source="image" label="Icon file" />
                <TextField source="order" label="Order" />
                {permissions >= 98 && <EditButton />}
                {permissions >= 99 && <SyncDeleteButton />}
            </Datagrid>
        </List>
    );
};

export const ServicesEdit = (props) => (
    <Edit {...props} title="Edit Service" transform={sanitizeService}>
        <SimpleForm toolbar={<SyncSaveToolbar />}>
            <TextInput source="name" label="Service name" validate={required()} fullWidth />
            <TextInput source="description" label="Description" multiline rows={3} fullWidth defaultValue="" />
            <NumberInput source="order" label="Display order" defaultValue={0} />
            <ServiceImageField />
            <TextInput source="image" label="Icon filename" fullWidth />
        </SimpleForm>
    </Edit>
);

export const ServicesCreate = (props) => (
    <Create {...props} title="New Service" transform={sanitizeService}>
        <SimpleForm toolbar={<SyncSaveToolbar />}>
            <TextInput source="name" label="Service name" validate={required()} fullWidth />
            <TextInput source="description" label="Description" multiline rows={3} fullWidth defaultValue="" />
            <NumberInput source="order" label="Display order" defaultValue={0} />
            <TextInput source="image" label="Icon filename (e.g. YachtDesign.png)" fullWidth defaultValue="" />
        </SimpleForm>
    </Create>
);
